import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  SpaceReadModel,
  useGetSpacesByIdAssociationsQuery,
  useGetSpacesByIdHierarchyQuery,
  usePutSpacesByIdAssociationsMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  isDefinedNumber,
  isDefinedString,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  toNumberString,
} from '@client/toolkit';

import { buildAncestorPathMap, SpacesAddModal } from '../../../../../components';

export function SpaceAssociationsCard() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { spaceId, buildingId } = useParams<{ buildingId: string; spaceId: string }>();

  const { data: associated, isFetching, isLoading, refetch } = useGetSpacesByIdAssociationsQuery(
    { id: spaceId ?? '' },
    { skip: !spaceId },
  );
  const [putAssociations, { isLoading: isSaving }] = usePutSpacesByIdAssociationsMutation();

  const [adding, setAdding] = useState(false);

  const selected = useMemo<SpaceReadModel[]>(() => associated ?? [], [associated]);

  const { data: hierarchyData } = useGetSpacesByIdHierarchyQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  const ancestorPathMap = useMemo(
    () => (hierarchyData ? buildAncestorPathMap(hierarchyData) : new Map<string, string>()),
    [hierarchyData],
  );

  const getLocationDisplay = (space: SpaceReadModel) =>
    ancestorPathMap.get(space.id) || space.parent?.name || space.building?.name || undefined;

  const handleSubmit = async (items: SpaceReadModel[]) => {
    if (!spaceId) return;
    try {
      await putAssociations({ id: spaceId, body: items.map((x) => x.id) }).unwrap();
      await refetch();
      setAdding(false);
    } catch {
      toast.error(tGlobal('space.associationsSaveError'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" variant="highlighted">
          {tGlobal('space.associations')}
        </CardTitle>
        <CardActions>
          <Button size="sm" color="primary-dark" onClick={() => setAdding(true)}>
            {selected.length > 0 ? tCommon('edit') : tCommon('add')}
          </Button>
        </CardActions>
      </CardHeader>

      <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
        {isLoading ? (
          <div className="space-y-3 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : (
        <Table className="w-full rounded-md" size="xs" layout="fixed">
          {!isFetching && selected.length === 0 && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>
                {tCommon('noEntitySpecified', {
                  entity: tGlobal('space.associations').toLowerCase(),
                })}
              </EmptyPlaceholder>
            </caption>
          )}
          <TableHead>
            <TableHeadRow>
              <TableHeadCell className="w-[30%]">{tGlobal('space.name')}</TableHeadCell>
              <TableHeadCell className="w-[20%]">{tGlobal('space.type')}</TableHeadCell>
              <TableHeadCell className="w-[15%]">{tGlobal('space.area')}</TableHeadCell>
              <TableHeadCell>{tCommon('location')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {selected.map((space) => (
              <TableRow key={space.id}>
                <TableCell>
                  <Text className="text-sm font-medium" isEmpty={!isDefinedString(space.name)}>
                    {space.name}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text className="text-sm" isEmpty={!isDefinedString(space.discriminator)}>
                    {space.discriminator ? tType(`SpaceDiscriminator.${space.discriminator}`) : '-'}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text className="text-sm" isEmpty={!isDefinedNumber(space.area?.metric)}>
                    {toNumberString(space.area?.metric ?? undefined, undefined, {
                      unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                    })}
                  </Text>
                </TableCell>
                <TableCell className="text-ellipsis overflow-hidden">
                  <Text
                    className="text-sm text-secondary whitespace-nowrap"
                    isEmpty={!isDefinedString(getLocationDisplay(space))}
                    title={getLocationDisplay(space)}
                  >
                    {getLocationDisplay(space)}
                  </Text>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}
      </CardContent>

      <SpacesAddModal
        open={adding}
        selected={selected}
        rootId={buildingId ?? undefined}
        showHierarchy
        requiredPackage={null}
        excludeIds={[spaceId, buildingId].filter(isDefinedString)}
        isSubmitting={isSaving}
        onClose={() => setAdding(false)}
        onSubmit={handleSubmit}
      />
    </Card>
  );
}
